import { type FunctionComponent, useMemo, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { useTopicType } from "scmp-app/components/topic/hooks";
import type { topicDescriptionTopic$key } from "scmp-app/queries/__generated__/topicDescriptionTopic.graphql";

import {
  ChineseTitle,
  Container,
  Description,
  DescriptionContainer,
  ExpandContainer,
  ImageIcon,
  ShowButton,
  Title,
  TitleContainer,
  TitleRow,
  Wrapper,
} from "./styles";
import { TopicMetadataDisplay } from "./topic-metadata-display";

type Props = {
  className?: string;
  reference: topicDescriptionTopic$key;
};

export const TopicDescription: FunctionComponent<Props> = ({ className, reference }) => {
  const topic = useFragment(
    graphql`
      fragment topicDescriptionTopic on Topic {
        ...topicHeaderRelatedLinksTopic
        ...entityFollowButtonBase
        ...hooksTopic
        ...topicMetadataDisplayTopic
        extraMetadata {
          __typename
        }
        ...entityLink
        mobileImage: image(type: MOBILE_APP_IMAGE) {
          style(filter: { style: "300x300" }) {
            url
          }
        }
        name
        description {
          text
        }
        chineseName
        sponsor {
          __typename
        }
      }
    `,
    reference,
  );

  const { currentType, hasSponsor } = useTopicType(topic);
  const isGeneralTopic = currentType === "general";
  const isShowImageIcon = !isGeneralTopic && !hasSponsor;

  const [isShowFullProfile, setIsShowFullProfile] = useState(false);
  const isHavingMoreContent = useMemo(() => {
    const textLength = topic?.description?.text?.length ?? 0;
    const extraMetadataLength = topic?.extraMetadata?.length ?? 0;
    return extraMetadataLength > 0 || textLength > 100;
  }, [topic?.description?.text?.length, topic?.extraMetadata?.length]);

  const containerReference = useRef<HTMLDivElement>(null);
  const defaultDescription = topic?.sponsor
    ? null
    : `The latest news, analysis and opinion on ${topic.name}. In-depth analysis, industry insights and expert opinion.`;

  const description = useMemo(
    () => findFirstNonEmptyString(topic.description?.text, defaultDescription),
    [topic.description?.text, defaultDescription],
  );

  return (
    <Container className={className} ref={containerReference}>
      <Wrapper>
        <TitleContainer>
          {isShowImageIcon && topic.mobileImage?.style?.url && (
            <ImageIcon
              $aspectRatio="1"
              alt={topic.name}
              lazyload={false}
              src={topic.mobileImage.style.url}
            />
          )}
          <TitleRow>
            <Title>{topic.name}</Title>
            {topic.chineseName && <ChineseTitle>{topic.chineseName}</ChineseTitle>}
          </TitleRow>
        </TitleContainer>
        <DescriptionContainer
          $isHavingMoreContent={isHavingMoreContent}
          $isShowFullProfile={isShowFullProfile}
        >
          {description && <Description>{description}</Description>}
          <ShowButton
            onClick={() => {
              setIsShowFullProfile(true);
            }}
          >
            SHOW FULL PROFILE
          </ShowButton>
          <ExpandContainer>
            <TopicMetadataDisplay reference={topic} />
          </ExpandContainer>
        </DescriptionContainer>
      </Wrapper>
    </Container>
  );
};

TopicDescription.displayName = "TopicDescription";
